import * as React from 'react';

import styles from './Concierge.module.scss';
import { CheckboxVisibility, DetailsList, IColumn, Link, Persona, PersonaSize, MessageBar, MessageBarType, Panel, PanelType, ActionButton } from '@fluentui/react';

import { ROUTES } from '../../config/routes';
import moment from 'moment-timezone';
import { SearchAgency, AgencyMetadata } from '../../redux/concierge/types';
import { PageHeader } from '../../components/core/PageHeader';
import { AgencyForm } from './AgencyForm';
import { HealAPI } from '../../healApi';
import { RootState } from '../../redux/types';
import { Dispatch, bindActionCreators } from 'redux';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { fetchAgencies } from '../../redux/concierge/actions';
import { connect } from 'react-redux';
import { NavigateFunction } from 'react-router-dom';
import { withRouter } from '../../util/Router';

declare const healConfig: any;

interface OwnProps {
  navigate: NavigateFunction;
}

interface StateProps {
  agencies?: SearchAgency[];
  isFetching: boolean;
}

interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    fetchAgencies: () => void;
  };
}

type ConciergeProps = OwnProps & StateProps & DispatchProps;

interface ConciergeState {
  columns: IColumn[];
  isSaving?: boolean;
  searchText: string;
  showPanel?: boolean;
}

class Component extends React.Component<ConciergeProps, ConciergeState> {
  constructor(props: ConciergeProps) {
    super(props);

    this.state = {
      columns: this.buildColumns(),
      searchText: '',
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Partners');
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Partners');
  }

  public render(): JSX.Element {
    return (
      <div>
        <PageHeader
          title="Partners"
          isLoading={this.props.isFetching}
          actions={{
            items: [
              {
                key: 'createPartner',
                text: 'Create partner',
                iconProps: { iconName: 'Add' },
                onClick: () => this.setState({ showPanel: true }),
              },
            ],
          }}
        />
        <DetailsList checkboxVisibility={CheckboxVisibility.hidden} items={this.props.agencies || []} setKey="set" columns={this.state.columns} />
        {this.renderSidePanel()}
        {this.renderNoResults()}
      </div>
    );
  }

  private renderNoResults(): JSX.Element | null {
    if (this.props.agencies && this.props.agencies.length === 0) {
      return (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={true}>
          There are no results.
        </MessageBar>
      );
    }
    return null;
  }

  private renderSidePanel(): JSX.Element {
    return (
      <Panel isOpen={this.state.showPanel} onDismiss={() => this.setState({ showPanel: false })} type={PanelType.medium} headerText="Create Partner">
        <AgencyForm saveAgency={this.createAgency} isSaving={this.state.isSaving} />
      </Panel>
    );
  }

  private createAgency = (group: AgencyMetadata) => {
    this.setState({ isSaving: true });
    HealAPI.createAgency(group).then((response) => {
      if (response.ok) {
        this.props.actions.fetchAgencies();
      }
      this.setState({ isSaving: false, showPanel: false });
    });
  };

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'name',
        name: 'Agency Name',
        minWidth: 80,
        maxWidth: 320,
        onRender: (item: SearchAgency) => {
          const onRenderPrimaryText = () => {
            return <Link onClick={() => this.props.navigate(`${ROUTES.agency}/${item.id}`)}>{item.name}</Link>;
          };
          return <Persona imageUrl={item.logoUrl} size={PersonaSize.size24} text={item.name} onRenderPrimaryText={onRenderPrimaryText} />;
        },
      },
      {
        key: 'createdAt',
        name: 'Created Date',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item: SearchAgency) => {
          if (item.createdAt) {
            const mtzStart = moment(item.createdAt);
            return mtzStart.format('MM/DD/YYYY');
          }
          return '-';
        },
      },
      {
        key: 'actAsAgency',
        name: '',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item: SearchAgency) => {
          return (
            <ActionButton
              className={styles.actAsAgencyBtn}
              iconProps={{ iconName: 'NavigateExternalInline' }}
              href={healConfig.agencyPortalBaseUrl + '?agency=' + item.name}
              target="_blank"
            >
              Act as agent
            </ActionButton>
          );
        },
      },
    ];
  }
}

export function mapStateToProps(state: RootState): StateProps {
  return {
    agencies: state.concierge.agencies,
    isFetching: state.concierge.isFetching,
  };
}

export function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    actions: bindActionCreators({ onPageDidMount, onPageWillUnmount, fetchAgencies }, dispatch),
  };
}

export const ConciergeContainer = withRouter(connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(Component));
